import { useCallback, useRef, useEffect } from "react";
import { GROUP_CHAT_HUB_WS_URL } from "../config/serviceConfig";

const MAX_RECONNECT_DELAY_MS = 30000;
const BASE_RECONNECT_DELAY_MS = 1000;
const SEND_WAIT_TIMEOUT_MS = 5000;

/**
 * 群聊 WebSocket Hook
 * - 单一 in-flight 连接 Promise，重复点击复用同一连接
 * - 指数退避自动重连，重连后幂等发送 reconnect
 * - 心跳由服务端 WebSocket Ping 控制
 */
const useGroupChatWS = ({
  wsRef,
  onRoomState,
  onRoomCreated,
  onGroupChatMsg,
  onAgentSpeakStream,
  onHostReadReceipt,
  onPhaseChange,
  onTrainingCompleted,
  onMemberStatus,
  onMemberSpeaking,
  onRoundUpdate,
  onScriptStatus,
  onReconnectResp,
  onDisconnect,
  onError,
  onArchiveSaved,
  onConnectionStatusChange,
  onAIQueueStatus,
  onCompetencyUpdate,
  onRobotReleased,
  getReconnectParams,
}) => {
  const handlersRef = useRef({});
  const connectPromiseRef = useRef(null);
  const shouldReconnectRef = useRef(false);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const intentionalCloseRef = useRef(false);
  const connectInternalRef = useRef(null);

  useEffect(() => {
    handlersRef.current = {
      onRoomState,
      onRoomCreated,
      onGroupChatMsg,
      onAgentSpeakStream,
      onHostReadReceipt,
      onPhaseChange,
      onTrainingCompleted,
      onMemberStatus,
      onMemberSpeaking,
      onRoundUpdate,
      onScriptStatus,
      onReconnectResp,
      onDisconnect,
      onError,
      onArchiveSaved,
      onConnectionStatusChange,
      onAIQueueStatus,
      onCompetencyUpdate,
      onRobotReleased,
      getReconnectParams,
    };
  });

  const clearReconnectTimer = () => {
    if (reconnectTimerRef.current != null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  const bindSocketHandlers = useCallback((ws) => {
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const h = handlersRef.current;

        switch (data.type) {
          case "room_created":
            if (data.roomId) {
              console.info("[GroupChat] roomId=", data.roomId);
            }
            h.onRoomCreated?.(data);
            break;
          case "room_state":
            if (data.roomId) {
              console.info("[GroupChat] roomId=", data.roomId);
            }
            h.onRoomState?.(data);
            break;
          case "GroupChatMsg":
            h.onGroupChatMsg?.(data);
            break;
          case "agent_speak_stream":
            h.onAgentSpeakStream?.(data);
            break;
          case "host_read_receipt":
            h.onHostReadReceipt?.(data);
            break;
          case "phase_change":
            h.onPhaseChange?.(data);
            break;
          case "training_completed":
            h.onTrainingCompleted?.(data);
            break;
          case "member_status":
            h.onMemberStatus?.(data);
            break;
          case "member_speaking":
            h.onMemberSpeaking?.(data);
            break;
          case "round_update":
            h.onRoundUpdate?.(data);
            break;
          case "script_status":
            h.onScriptStatus?.(data);
            break;
          case "reconnect_response":
            h.onReconnectResp?.(data);
            break;
          case "ai_queue_status":
            h.onAIQueueStatus?.(data);
            break;
          case "competency_update":
            h.onCompetencyUpdate?.(data);
            break;
          case "error":
            h.onError?.(data.message || "未知错误");
            break;
          case "archive_saved":
            h.onArchiveSaved?.(data.message || "存档已保存");
            break;
          case "robot_released":
            h.onRobotReleased?.(data);
            break;
          default:
            console.log("[GroupChatHub] 未处理的消息类型:", data.type);
        }
      } catch (e) {
        console.error("[GroupChatHub] 解析消息失败:", e);
      }
    };

    ws.onerror = (err) => {
      console.error("[GroupChatHub] WebSocket 错误:", err);
    };
  }, []);

  const sendReconnectIfNeeded = useCallback((ws) => {
    const params = handlersRef.current.getReconnectParams?.();
    if (!params || !params.roomId || !params.userId) return;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(
      JSON.stringify({
        type: "reconnect",
        roomId: params.roomId,
        userId: params.userId,
        lastSeq: params.lastSeq || 0,
        clientViewMode: params.clientViewMode || "normal",
      })
    );
    console.log("[GroupChatHub] 重连后已发送 reconnect", {
      roomId: params.roomId,
      userId: params.userId,
      lastSeq: params.lastSeq || 0,
    });
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (!shouldReconnectRef.current || intentionalCloseRef.current) return;
    clearReconnectTimer();
    const attempt = reconnectAttemptRef.current;
    const delay = Math.min(
      BASE_RECONNECT_DELAY_MS * Math.pow(2, attempt),
      MAX_RECONNECT_DELAY_MS
    );
    reconnectAttemptRef.current = attempt + 1;
    console.log(`[GroupChatHub] ${delay}ms 后自动重连 (attempt=${attempt + 1})`);
    handlersRef.current.onConnectionStatusChange?.("connecting");
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      if (!shouldReconnectRef.current || intentionalCloseRef.current) return;
      const fn = connectInternalRef.current;
      if (!fn) return;
      fn(true).catch((err) => {
        console.error("[GroupChatHub] 自动重连失败:", err);
      });
    }, delay);
  }, []);

  const connectInternal = useCallback((isAutoReconnect = false) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      handlersRef.current.onConnectionStatusChange?.("connected");
      return Promise.resolve(wsRef.current);
    }
    if (connectPromiseRef.current) {
      return connectPromiseRef.current;
    }

    shouldReconnectRef.current = true;
    intentionalCloseRef.current = false;
    handlersRef.current.onConnectionStatusChange?.("connecting");

    connectPromiseRef.current = new Promise((resolve, reject) => {
      let settled = false;
      const ws = new WebSocket(GROUP_CHAT_HUB_WS_URL);
      wsRef.current = ws;
      bindSocketHandlers(ws);

      ws.onopen = () => {
        console.log("[GroupChatHub] WebSocket 已连接");
        reconnectAttemptRef.current = 0;
        connectPromiseRef.current = null;
        settled = true;
        handlersRef.current.onConnectionStatusChange?.("connected");
        if (isAutoReconnect) {
          sendReconnectIfNeeded(ws);
        }
        resolve(ws);
      };

      ws.onclose = () => {
        console.log("[GroupChatHub] WebSocket 已断开");
        if (wsRef.current === ws) {
          wsRef.current = null;
        }
        connectPromiseRef.current = null;
        handlersRef.current.onConnectionStatusChange?.("disconnected");
        if (!intentionalCloseRef.current) {
          handlersRef.current.onDisconnect?.();
          scheduleReconnect();
        }
        if (!settled) {
          settled = true;
          reject(new Error("群聊 WebSocket 连接失败"));
        }
      };
    });

    return connectPromiseRef.current;
  }, [wsRef, bindSocketHandlers, sendReconnectIfNeeded, scheduleReconnect]);

  connectInternalRef.current = connectInternal;

  const connect = useCallback((onOpen) => {
    return connectInternal(false).then((ws) => {
      onOpen?.();
      return ws;
    }).catch((err) => {
      handlersRef.current.onError?.(err.message || "群聊连接失败");
      throw err;
    });
  }, [connectInternal]);

  const send = useCallback(
    (msg) => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(msg));
        return true;
      }
      // 连接已在建立中时，等待本次连接成功后补发一次，避免用户在重连窗口内丢失发言。
      const pendingConnection = connectPromiseRef.current;
      if (pendingConnection) {
        let timeoutId;
        const timeout = new Promise((_, reject) => {
          timeoutId = setTimeout(
            () => reject(new Error("群聊重连等待超时")),
            SEND_WAIT_TIMEOUT_MS
          );
        });
        Promise.race([pendingConnection, timeout])
          .then((ws) => {
            if (ws && ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify(msg));
              return;
            }
            handlersRef.current.onError?.("群聊连接已断开，请稍后重发");
          })
          .catch(() => {
            handlersRef.current.onError?.("群聊连接已断开，请稍后重发");
          })
          .finally(() => clearTimeout(timeoutId));
        return true;
      }
      handlersRef.current.onError?.("群聊连接已断开，请先重新连接");
      return false;
    },
    [wsRef]
  );

  const sendReconnect = useCallback(
    (roomId, userId, lastSeq, clientViewMode) => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && roomId && userId) {
        wsRef.current.send(
          JSON.stringify({
            type: "reconnect",
            roomId,
            userId,
            lastSeq: lastSeq || 0,
            clientViewMode: clientViewMode || "normal",
          })
        );
        return true;
      }
      handlersRef.current.onError?.("群聊连接已断开，请先重新连接");
      return false;
    },
    [wsRef]
  );

  const disconnect = useCallback(() => {
    intentionalCloseRef.current = true;
    shouldReconnectRef.current = false;
    clearReconnectTimer();
    reconnectAttemptRef.current = 0;
    connectPromiseRef.current = null;
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    handlersRef.current.onConnectionStatusChange?.("idle");
  }, [wsRef]);

  useEffect(() => () => {
    intentionalCloseRef.current = true;
    shouldReconnectRef.current = false;
    clearReconnectTimer();
  }, []);

  return { connect, send, sendReconnect, disconnect };
};

export default useGroupChatWS;
